import React from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import styled from 'styled-components';
import { PortableText } from '@portabletext/react';
import urlBuilder from '@sanity/image-url';
import { getImageDimensions } from '@sanity/asset-utils';
import SEO from '../components/SEO';
import DealerForm from '../components/DealerForm';

const SupportStyled = styled.div`
  max-width: calc(var(--wrapper) * 0.5);
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 3rem;
  h3 {
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    padding: 4rem;
    margin: 2rem 0;
    text-align: center;
  }
  input.needsclick {
    border: 1px solid rgb(96, 106, 114);
    border-bottom: 1px solid rgb(96, 106, 114) !important;
  }
  h6 {
    border-top: 1px solid var(--border);
    text-transform: normal;
    padding-top: 2rem;
  }
  .bc-size-chart,
  .table-wrapper {
    overflow-x: auto;
    white-space: nowrap;
  }
  .table-wrapper > table,
  .table-wrapper > table th,
  .table-wrapper > table td {
    padding: 5px;
    font-size: 13px;
    border: 1px solid #eee !important;
    border-collapse: collapse;
    table-layout: auto;
  }
  .table-wrapper > table {
    margin-bottom: 5px;
  }

  .table-wrapper > table th {
    background: var(--greyLight);
    text-align: right;
    position: sticky;
    left: 0;
  }

  .table-wrapper > table td {
    text-align: center;
  }
`;

// Barebones lazy-loaded image component
const SampleImageComponent = ({ value }) => {
  const { width, height } = getImageDimensions(value);
  return (
    <img
      src={urlBuilder().image(value).width(800).fit('max').auto('format').url()}
      alt={value.alt || ' '}
      loading="lazy"
      style={{
        // Avoid jumping around with aspect-ratio CSS property
        aspectRatio: width / height,
      }}
    />
  );
};

const serializers = {
  block: {
    h1: ({ children }) => <h1 className="heading-h1">{children}</h1>,
    h2: ({ children }) => <h2 className="heading-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="heading-h3">{children}</h3>,
    h4: ({ children }) => <h4 className="heading-h4">{children}</h4>,
    h5: ({ children }) => <h5 className="heading-h5">{children}</h5>,
    h6: ({ children }) => <h6 className="heading-h6">{children}</h6>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
    quote: ({ children }) => <blockquote>{children}</blockquote>,
    list: {
      bullet: ({ children }) => <ul>{children}</ul>,
      number: ({ children }) => <ol>{children}</ol>,
    },
    listItem: {
      bullet: ({ children }) => <li>{children}</li>,
    },
    marks: {
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      code: ({ children }) => <code>{children}</code>,
    },
    types: {
      image: SampleImageComponent,
    },
  },
};

export default function SingleSupportPage({ data, pageContext }) {
  const { support, sizeCharts } = data;
  const charts = sizeCharts?.nodes || [];
  return (
    <>
      <SEO title={pageContext.title} />
      <SupportStyled>
        <div className="wrapper">
          <h3>{pageContext.title}</h3>
          <PortableText
            value={pageContext.content}
            projectId="9xe1g1l8"
            dataset="production"
            components={serializers}
          />
        </div>
        {pageContext.title === 'Dealer enquiries' && <DealerForm />}
        {pageContext.title === 'Shoe Size Finder' && (
          <div className="bc-size-chart">
            {charts.length === 0 && (
              <p>No size charts found. Please try again later.</p>
            )}

            {charts.map((chartNode) => {
              const { handle, title, series, chart } = chartNode;

              if (!Array.isArray(chart)) return null;

              return (
                <section key={handle || title}>
                  <p>
                    <strong>{series || title}</strong>
                  </p>
                  <div className="table-wrapper">
                    <table>
                      <tbody>
                        {chart.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            <th>{row.title}</th>
                            {row.values?.map((value, cellIndex) => (
                              <td key={cellIndex}>{value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </SupportStyled>
    </>
  );
}

export const query = graphql`
  query ($slug: String!) {
    support: sanitySupport(
      supportPages: { elemMatch: { slug: { current: { eq: $slug } } } }
    ) {
      supportPages {
        title
        slug {
          current
        }
        _rawContent
      }
    }
    sizeCharts: allShopifySizeChart {
      nodes {
        handle
        title
        series
        chart {
          title
          values
        }
      }
    }
  }
`;
